import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { onEvent } from "../lib/realtime";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n.jsx";
import { PageHeader, Empty } from "../components/Bits";
import TransInput from "../components/TransInput";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, ChevronRight, ChevronDown, Archive, ArchiveRestore, FolderTree, Pencil } from "lucide-react";

const ENTITY = { group: "work-groups", sub: "work-subgroups", work: "work-types" };

export default function Works() {
  const { t, tn } = useI18n();
  const { can } = useAuth();
  const [groups, setGroups] = useState([]);
  const [subsBy, setSubsBy] = useState({});
  const [worksBy, setWorksBy] = useState({});
  const [units, setUnits] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [showArchived, setShowArchived] = useState(false);
  const [modal, setModal] = useState(null); // {level, parent, item}
  const [form, setForm] = useState({});
  const canEdit = can("works.create") || can("works.edit");
  const arch = showArchived ? "include_archived=true" : "";

  const loadGroups = () => api.get(`/config/work-groups?${arch}`).then((r) => setGroups(r.data));
  const loadSubs = async (gid) => { const { data } = await api.get(`/config/work-subgroups?group_id=${gid}&${arch}`); setSubsBy((s) => ({ ...s, [gid]: data })); };
  const loadWorks = async (sid) => { const { data } = await api.get(`/config/work-types?subgroup_id=${sid}&${arch}`); setWorksBy((w) => ({ ...w, [sid]: data })); };
  const reloadAll = () => { loadGroups(); Object.keys(subsBy).forEach(loadSubs); Object.keys(worksBy).forEach(loadWorks); };

  useEffect(() => { loadGroups(); api.get("/config/units").then((r) => setUnits(r.data)); }, [showArchived]); // eslint-disable-line
  useEffect(() => onEvent((ev) => { if (ev.type === "config.changed" && String(ev.payload?.entity).startsWith("work")) reloadAll(); }), [subsBy, worksBy, showArchived]); // eslint-disable-line

  const toggleGroup = async (g) => { setExpanded((e) => ({ ...e, [g.id]: !e[g.id] })); if (!subsBy[g.id]) await loadSubs(g.id); };
  const toggleSub = async (s) => { setExpanded((e) => ({ ...e, [s.id]: !e[s.id] })); if (!worksBy[s.id]) await loadWorks(s.id); };

  const openCreate = (level, parent) => { setModal({ level, parent }); setForm({ name: {}, description: {}, unit_id: units[0]?.id || "", item_number: "", order: 0, measurement_type: "QUANTITY_BASED", activity_type: "PRODUCTIVE" }); };
  const openEdit = (level, item, parent) => { setModal({ level, parent, item }); setForm({ name: item.name || {}, description: item.description || {}, unit_id: item.unit_id || "", item_number: item.item_number || "", order: item.order ?? 0, icon: item.icon, measurement_type: item.measurement_type || "QUANTITY_BASED", activity_type: item.activity_type || "PRODUCTIVE" }); };

  const save = async () => {
    const { level, parent, item } = modal;
    const body = { name: form.name, description: form.description, order: Number(form.order) || 0 };
    if (level === "group") body.icon = form.icon || "folder";
    if (level === "sub") body.group_id = parent?.id || item?.group_id;
    if (level === "work") Object.assign(body, { subgroup_id: parent?.id || item?.subgroup_id, group_id: parent?.group_id || item?.group_id, unit_id: form.unit_id, item_number: form.item_number.trim(), efficiency_method: "norm_and_cost", measurement_type: form.measurement_type, activity_type: form.activity_type });
    try {
      if (item) await api.put(`/config/${ENTITY[level]}/${item.id}`, body);
      else await api.post(`/config/${ENTITY[level]}`, body);
      if (level === "group") loadGroups(); else if (level === "sub") loadSubs(body.group_id); else loadWorks(body.subgroup_id);
      toast.success(t("save")); setModal(null);
    } catch (e) { toast.error(e.response?.data?.detail || t("error")); }
  };
  const archiveToggle = async (level, item, reload) => {
    await api.delete(`/config/${ENTITY[level]}/${item.id}${item.archived ? "?restore=true" : ""}`); reload();
  };

  const Row = ({ level, item, parent, reload, children, className }) => (
    <div className={`flex items-center gap-2 px-2 py-2 hover:bg-[var(--bg)] rounded-lg min-w-0 flex-wrap sm:flex-nowrap ${item.archived ? "opacity-50" : ""} ${className || ""}`}>
      {children}
      {item.archived && <span className="text-[10px] uppercase text-[var(--text-3)]">{t("archived")}</span>}
      {canEdit && <button onClick={() => openEdit(level, item, parent)} className="text-[var(--blue)]" data-testid={`edit-${level}-${item.id}`}><Pencil size={14} /></button>}
      {canEdit && <button onClick={() => archiveToggle(level, item, reload)} className="text-[var(--red)]" data-testid={`archive-${level}-${item.id}`}>{item.archived ? <ArchiveRestore size={14} /> : <Archive size={14} />}</button>}
    </div>
  );

  return (
    <div>
      <PageHeader title={t("works")} subtitle={t("app_tag")}
        action={<div className="flex items-center gap-3">
          <label className="text-sm flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="show-archived" /> {t("show_archived")}</label>
          {canEdit && <Button data-testid="add-group-btn" onClick={() => openCreate("group")} className="bg-[var(--amber)] hover:bg-[var(--amber-2)] text-white gap-2"><Plus size={16} /> {t("work_group")}</Button>}
        </div>} />
      {groups.length === 0 ? <Empty /> : (
        <div className="tactical-card p-2">
          {groups.map((g) => (
            <div key={g.id}>
              <Row level="group" item={g} reload={loadGroups}>
                <button onClick={() => toggleGroup(g)} data-testid={`toggle-grp-${g.id}`}>{expanded[g.id] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</button>
                <FolderTree size={16} className="text-[var(--amber)]" />
                <span className="font-display font-bold flex-1 min-w-[120px] break-words">{tn(g.name)}</span>
                {canEdit && <button onClick={() => openCreate("sub", g)} className="text-[var(--blue)] text-sm" data-testid={`add-sub-${g.id}`}>+ {t("subgroup")}</button>}
              </Row>
              {expanded[g.id] && (subsBy[g.id] || []).map((s) => (
                <div key={s.id} className="ml-3 sm:ml-6">
                  <Row level="sub" item={s} parent={g} reload={() => loadSubs(g.id)}>
                    <button onClick={() => toggleSub(s)} data-testid={`toggle-sub-${s.id}`}>{expanded[s.id] ? <ChevronDown size={15} /> : <ChevronRight size={15} />}</button>
                    <span className="font-semibold flex-1 min-w-[120px] break-words">{tn(s.name)}</span>
                    {canEdit && <button onClick={() => openCreate("work", s)} className="text-[var(--blue)] text-sm" data-testid={`add-work-${s.id}`}>+ {t("work")}</button>}
                  </Row>
                  {expanded[s.id] && (worksBy[s.id] || []).map((w) => (
                    <Row key={w.id} level="work" item={w} parent={s} reload={() => loadWorks(s.id)} className="ml-3 sm:ml-8 text-sm border-l-2 border-[var(--border)] py-1.5">
                      {w.item_number && <span className="font-mono-num text-xs text-[var(--text-3)] w-16" data-testid={`item-number-${w.id}`}>{w.item_number}</span>}
                      <span className="flex-1 min-w-[140px] break-words">{tn(w.name)}</span>
                      {w.measurement_type === "TIME_BASED" && <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-[var(--bg)] text-[var(--text-3)] shrink-0">{t("TIME_BASED")}</span>}
                      {w.activity_type === "NON_PRODUCTIVE" && <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-[var(--bg)] text-[var(--amber)] shrink-0">{t("NON_PRODUCTIVE")}</span>}
                      <span className="text-xs text-[var(--text-3)] shrink-0">{tn(units.find((u) => u.id === w.unit_id)?.name)}</span>
                    </Row>
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!modal} onOpenChange={() => setModal(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{modal?.item ? t("edit") : t("create")} — {modal?.level === "group" ? t("work_group") : modal?.level === "sub" ? t("subgroup") : t("work")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><div className="text-[11px] uppercase font-semibold text-[var(--text-3)] mb-1">{t("name")}</div>
              <TransInput value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} placeholder={t("name")} /></div>
            <div><div className="text-[11px] uppercase font-semibold text-[var(--text-3)] mb-1">{t("description")}</div>
              <TransInput value={form.description} onChange={(v) => setForm((f) => ({ ...f, description: v }))} placeholder={t("description")} /></div>
            <div className="grid grid-cols-2 gap-2">
              {modal?.level === "work" && (<>
                <div><div className="text-[11px] uppercase font-semibold text-[var(--text-3)] mb-1">{t("item_number")}</div>
                  <Input data-testid="work-item-number" value={form.item_number} onChange={(e) => setForm((f) => ({ ...f, item_number: e.target.value }))} className="h-10" /></div>
                <div><div className="text-[11px] uppercase font-semibold text-[var(--text-3)] mb-1">{t("unit")}</div>
                  <select className="border rounded-md h-10 px-2 text-sm w-full" value={form.unit_id} onChange={(e) => setForm((f) => ({ ...f, unit_id: e.target.value }))} data-testid="work-unit-select">
                    {units.map((u) => <option key={u.id} value={u.id}>{tn(u.name)}</option>)}
                  </select></div>
              </>)}
              {modal?.level === "work" && (<>
                <div><div className="text-[11px] uppercase font-semibold text-[var(--text-3)] mb-1">{t("measurement_type")}</div>
                  <select className="border rounded-md h-10 px-2 text-sm w-full" value={form.measurement_type} onChange={(e) => setForm((f) => ({ ...f, measurement_type: e.target.value }))} data-testid="work-measurement-type">
                    {["QUANTITY_BASED", "TIME_BASED"].map((k) => <option key={k} value={k}>{t(k)}</option>)}
                  </select></div>
                <div><div className="text-[11px] uppercase font-semibold text-[var(--text-3)] mb-1">{t("activity_type")}</div>
                  <select className="border rounded-md h-10 px-2 text-sm w-full" value={form.activity_type} onChange={(e) => setForm((f) => ({ ...f, activity_type: e.target.value }))} data-testid="work-activity-type">
                    {["PRODUCTIVE", "NON_PRODUCTIVE"].map((k) => <option key={k} value={k}>{t(k)}</option>)}
                  </select></div>
              </>)}
              <div><div className="text-[11px] uppercase font-semibold text-[var(--text-3)] mb-1">{t("order")}</div>
                <Input data-testid="work-order" type="number" value={form.order} onChange={(e) => setForm((f) => ({ ...f, order: e.target.value }))} className="h-10" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(null)}>{t("cancel")}</Button>
            <Button onClick={save} className="bg-[var(--amber)] hover:bg-[var(--amber-2)] text-white" data-testid="work-save">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
